/**
 * F-131 — the finance KPI rows' formatting (display only).
 *
 * The rows come from the server's stored result (the cockpit's five vitals, scored there); this
 * file only formats a figure and its target for the screen. The thresholds an org has set in
 * Finance (`financial_benchmarks`) are applied on the server, so the target shown here is the
 * one the cockpit uses.
 */
import type { Metric } from '@/types/tracking-view';

export const FINANCE_VITALS = [
  'gross-margin',
  'net-margin',
  'overhead-recovery',
  'cash-runway',
  'debtor-days',
] as const;
export type FinanceVitalId = (typeof FINANCE_VITALS)[number];

/**
 * Labels, units and the cockpit's default thresholds, so a row still carries its name and
 * target when the cockpit has no metric for it (greyed, never blank).
 */
export const VITAL_DEFAULTS: Record<
  FinanceVitalId,
  { name: string; unit: Metric['unit']; green: number; amber: number; lowerIsBetter: boolean }
> = {
  'gross-margin': { name: 'Gross margin', unit: 'percent', green: 40, amber: 35, lowerIsBetter: false },
  'net-margin': { name: 'Net margin (pre-tax)', unit: 'percent', green: 10, amber: 8, lowerIsBetter: false },
  'overhead-recovery': { name: 'Overhead recovery', unit: 'ratio', green: 1.3, amber: 1.0, lowerIsBetter: false },
  'cash-runway': { name: 'Cash runway', unit: 'months', green: 3, amber: 1.5, lowerIsBetter: false },
  'debtor-days': { name: 'Debtor days', unit: 'days', green: 45, amber: 60, lowerIsBetter: true },
};

function isVital(id: string): id is FinanceVitalId {
  return (FINANCE_VITALS as readonly string[]).includes(id);
}

/** The figure for the screen, or null when there is none. */
export function formatKpiValue(kpi: { value: number | null; unit: Metric['unit'] | string }): string | null {
  if (kpi.value === null || !Number.isFinite(kpi.value)) return null;
  const v = kpi.value;
  switch (kpi.unit) {
    case 'percent':
      return `${v.toFixed(1)}%`;
    case 'months':
      return `${v.toFixed(1)} mo`;
    case 'days':
      return `${Math.round(v)} days`;
    case 'ratio':
      return v.toFixed(2);
    case 'currency':
      return `$${Math.round(v).toLocaleString('en-AU')}`;
    default:
      return String(v);
  }
}

/**
 * The target line, always: the row's thresholds when the cockpit has them, else the row's own
 * target (a catalogue row carries its green as min or max), else the vitals' defaults, else the
 * workbook's wording.
 */
export function formatKpiTarget(kpi: {
  id: string;
  unit: Metric['unit'] | string;
  thresholds?: { green: number };
  target?: { min?: number; max?: number };
  targetText?: string | null;
}): string {
  const defaults = isVital(kpi.id) ? VITAL_DEFAULTS[kpi.id] : null;
  const green = kpi.thresholds?.green ?? kpi.target?.min ?? kpi.target?.max ?? defaults?.green;
  if (green === undefined) return kpi.targetText ?? '';
  const lowerIsBetter = kpi.target
    ? kpi.target.max !== undefined && kpi.target.min === undefined
    : defaults?.lowerIsBetter ?? false;
  const unit = kpi.unit ?? defaults?.unit;
  const value = unit === 'ratio' ? green.toFixed(2) : unit === 'currency' ? `$${Math.round(green).toLocaleString('en-AU')}` : String(green);
  const suffix = unit === 'percent' ? '%' : unit === 'months' ? ' mo' : unit === 'days' ? ' days' : '';
  return `${lowerIsBetter ? '≤' : '≥'} ${value}${suffix}`;
}
